import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { safeStr } from "common/api";
import { TIMEWINDOW_MAX_MAP } from "common/constants";
import { useTimeOffset } from "common/hooks/useTimeOffset";
import Switch from "@mui/material/Switch";
import Charts from "./Charts";
import { blockingWait, getIndexes } from "common/utils";
import SelectAutocomplete from "common/SelectAutocomplete";
import { getParsedValue, getSumValue } from "./utils";
import BqnContext from "common/BqnContext";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const ContextMenuWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: space-between;

  .targetInput .input-group {
    margin-bottom: 0px;
  }

  @media (max-width: 1300px) {
    display: flex;
    flex-wrap: wrap;

    .targetInput {
      width: 100%;
    }
  }
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const SelectsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  .subgroupSelect {
    width: 200px;
  }
  .outputSelect {
    width: 110px;
  }
  .timeSelect {
    width: 190px;
  }
  .displaySelect {
    width: 200px;
  }
`;

const LiveModeWrapper = styled.div`
  width: 130px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .live-mode-switch.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const LabelSwitch = styled.div`
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
`;

const _outputScopeChoices = [
  { value: "ip-addresses", label: "IP-Addr" },
  { value: "subscriber-ids", label: "Subs-Ids" },
];

const _timeWindowChoices = [
  { value: "2", label: "2 min. time span" },
  { value: "200", label: "2 min time-span, no time-out" },
  { value: "5", label: "5 min. time span" },
  { value: "500", label: "5 min time-span, no time-out" },
  { value: "10", label: "10 min. time span" },
  { value: "1000", label: "10 min time-span, no time-out" },
];

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink" },
  { value: "downall", label: "Downlink & LAT & RTX" },
  { value: "upall", label: "Uplink & LAT & RTX " },
];

const ContextMenu = ({
  timeWindow,
  setTimeWindow,
  liveMode,
  setLiveMode,
  output,
  setOutput,
  subscriberGroup,
  setSubscriberGroup,
  display,
  setDisplay,
}) => {
  const [subscribergroupOptions, setSubscribergroupOptions] = useState([]);

  useEffect(() => {
    ifCl.run("show api subscriber-group").then((response) => {
      const [head, ...lines] = response.trim().split("\n");
      const groups = lines.reduce((acc, line) => {
        const [_, nameGroup] = line.trim().split(/\s+/);
        if (nameGroup !== "all-subscribers" && !acc.has(nameGroup)) {
          acc.add(nameGroup);
        }
        return acc;
      }, new Set([]));
      const options = [...groups].map((group) => {
        return { value: group, label: group };
      });

      setSubscribergroupOptions([
        { value: "all-subscribers", label: "All subscribers" },
        ...options,
      ]);
    });
  }, []);

  return (
    <ContextMenuWrapper>
      <SelectsWrapper>
        <SelectWrapper className="displaySelect">
          <SelectInputWithIcon
            title="Display"
            name="Display"
            icon="apps"
            selected={display}
            onChange={({ target }) => setDisplay(target.value)}
            options={_displayChoices}
          />
        </SelectWrapper>
        <SelectWrapper className="outputSelect">
          <SelectInputWithIcon
            title="Outcome grouping"
            name="output"
            selected={output}
            onChange={({ target }) => setOutput(target.value)}
            options={_outputScopeChoices}
          />
        </SelectWrapper>
        <SelectWrapper className="timeSelect">
          <SelectInputWithIcon
            title="Time Span"
            name="Time Span"
            icon="crop_16_9"
            selected={timeWindow}
            onChange={({ target }) => setTimeWindow(parseInt(target.value))}
            options={_timeWindowChoices}
          />
        </SelectWrapper>
        {subscribergroupOptions.length > 0 ? (
          <SelectWrapper className="subgroupSelect">
            <SelectAutocomplete
              title="Subscriber Group"
              name="SubscriberGroup2"
              icon="group"
              selected={subscriberGroup}
              onChange={(value) => setSubscriberGroup(value)}
              options={subscribergroupOptions}
            />
          </SelectWrapper>
        ) : null}
      </SelectsWrapper>
      <LiveModeWrapper>
        <LabelSwitch>Live mode</LabelSwitch>
        <Switch
          className="live-mode-switch"
          checked={liveMode === "on"}
          onChange={(e) => {
            if (e.target.checked) {
              setLiveMode("clear");
            } else {
              setLiveMode("off");
            }
          }}
        />
      </LiveModeWrapper>
    </ContextMenuWrapper>
  );
};

function getFieldsAndMap(
  newFieldNamesSet,
  oldFieldsMap,
  colorsSet,
  refColorIndex,
  parsedResponse,
  time,
  timeWindow
) {
  const { map, fields } = [...newFieldNamesSet].reduce(
    (acc, subscriber) => {
      const entry = oldFieldsMap[subscriber];
      if (!entry) {
        let newColor;
        if (subscriber === "rest") {
          newColor = colorsSet[colors.length - 1];
        } else {
          newColor = colorsSet[refColorIndex.current];
        }
        refColorIndex.current =
          (refColorIndex.current + 1) % (colorsSet.length - 1);
        const { downlink, uplink } = parsedResponse[subscriber];
        const total = getSumValue(downlink) + getSumValue(uplink);
        const newField = {
          name: subscriber,
          label: subscriber,
          color: newColor,
          value: total,
        };
        acc.fields.push(newField);
        acc.map[subscriber] = {
          color: newColor,
          time,
          total,
          totalUp: getSumValue(uplink),
          totalDown: getSumValue(downlink),
        };
      } else {
        const timeFieldNotFound = time.getTime() - entry.time.getTime();
        if (parsedResponse[subscriber]) {
          const { downlink, uplink } = parsedResponse[subscriber];
          const total = getSumValue(downlink) + getSumValue(uplink);
          acc.map[subscriber] = {
            ...entry,
            time,
            total: oldFieldsMap[subscriber].total + total,
            totalUp: oldFieldsMap[subscriber].totalUp + getSumValue(uplink),
            totalDown:
              oldFieldsMap[subscriber].totalDown + getSumValue(downlink),
          };
          const newField = {
            name: subscriber,
            label: subscriber,
            color: entry.color,
            value: total,
          };
          acc.fields.push(newField);
        } else if (timeFieldNotFound <= TIMEWINDOW_MAX_MAP[timeWindow]) {
          acc.map[subscriber] = entry;
          const newField = {
            name: subscriber,
            label: subscriber,
            color: entry.color,
            value: 0,
          };
          acc.fields.push(newField);
        }
      }
      return acc;
    },
    { map: {}, fields: [] }
  );

  return { map, fields: fields.sort() };
}

const getValue = (item, type) => {
  if (item === null) {
    return null;
  }
  const { time } = item;
  return Object.entries(item).reduce(
    (acc, [key, value]) => {
      if (key !== "time") {
        const linkKey = `${type}link`;
        const latMinKey = `${type}rttmin`;
        const latMedKey = `${type}rttmed`;
        const lat80Key = `${type}rtt80`;
        const rtxKey = `${type}rtx`;
        if (type === "down") {
          acc[key] = {
            [linkKey]: getParsedValue(value[linkKey]),
            [latMinKey]: getParsedValue(value[latMinKey]),
            [latMedKey]: getParsedValue(value[latMedKey]),
            [lat80Key]: getParsedValue(value[lat80Key]),
            [rtxKey]: getParsedValue(value[rtxKey]),
          };
        } else {
          acc[key] = {
            [linkKey]: getParsedValue(value[linkKey]),
            [latMinKey]: getParsedValue(value[latMinKey]),
            [rtxKey]: getParsedValue(value[rtxKey]),
          };
        }
      }

      return acc;
    },
    { time }
  );
};

function getLiveThrougputData({ output, subscriberGroup, mode, bqnId }) {
  const isBqnSpec = !(mode === "standalone" || bqnId === "all");
  const bqnSpecCli = isBqnSpec ? ` bqn-id ${bqnId}` : "";
  if (subscriberGroup === "all-subscribers") {
    if (output === "subscriber-ids") {
      return ifCl.run(
        `show subscriber all rates subscriber-id lines 19${bqnSpecCli}`
      );
    }
    return ifCl.run(`show subscriber all rates lines 19${bqnSpecCli}`);
  }
  if (output === "subscriber-ids") {
    return ifCl.run(
      `show subscriber all rates subscriber-id subscriber-group ${safeStr(
        subscriberGroup
      )} lines 19${bqnSpecCli}`
    );
  }
  return ifCl.run(
    `show subscriber all rates subscriber-group ${safeStr(
      subscriberGroup
    )} lines 19${bqnSpecCli}`
  );
}

function parseThroughputResponse(response) {
  const [header, ...rows] = response.trim().split("\n");

  if (!header || !rows) {
    return {};
  }

  return rows.reduce(
    (acc, value) => {
      if (value && value.length > 0) {
        const [field, downlink, uplink, downrttmin, downrttmed, downrtt80, uprttmin, downrtx, uprtx] =
          value.trim().split(/[ ,]+/);
        if (field !== "Total") {
          const key = field === "Rest" ? "rest" : field;
          acc.series[key] = {
            downlink,
            uplink,
            downrttmin,
            downrttmed,
            downrtt80,
            uprttmin,
            downrtx,
            uprtx,
          };
        } else {
          acc.totalUp = { 
            link: uplink, 
            rttmin: uprttmin, 
            rtx: uprtx 
          };
          acc.totalDown = {
            link: downlink,
            rttmin: downrttmin,
            rttmed: downrttmed,
            rtt80: downrtt80,
            rtx: downrtx,
          };
        }
      }
      return acc;
    },
    { series: {}, totalUp: undefined, totalDown: undefined }
  );
}

const useTime = (counting) => {
  const [seconds, setSeconds] = useState(0);
  const timeout = useRef();

  useEffect(() => {
    increaseSeconds();
  }, [seconds]);

  useEffect(() => {
    if (!counting) {
      clearTimeout(timeout.current);
    } else {
      setSeconds(0);
    }
    return () => clearTimeout(timeout.current);
  }, [counting]);

  function increaseSeconds() {
    if (document.visibilityState === "visible") {
      timeout.current = setTimeout(() => {
        setSeconds((seconds) => seconds + 1);
      }, 1000);
    } else {
      blockingWait(1000);
      setSeconds((seconds) => seconds + 1);
    }
  }

  return [seconds, setSeconds];
};

const SubscriberThroughputLiveReport = (params) => {
  const [display, setDisplay] = useState(
    params.direction === "uplink" ? "upall" : "downall"
  );
  const [subscriberGroup, setSubscriberGroup] = useState(
    params.subscriberGroup ?? "all-subscribers"
  );
  const [output, setOutput] = useState("ip-addresses");
  const [timeWindow, setTimeWindow] = useState(2);
  const [liveMode, setLiveMode] = useState("on");
  const [isFetching, setIsFetching] = useState(false);
  const { timeOffset } = useTimeOffset();
  const [subs, setSubs] = useState({ map: {}, fields: [] });
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const [mouseEvent, setMouseEvent] = useState();
  const [item, setItem] = useState(null);
  const [initTime, setInitTime] = useState();
  const [seconds, setSeconds] = useTime(liveMode === "on");
  const [total, setTotal] = useState({ totalUp: {}, totalDown: {} });
  const refColorIndex = useRef(0);
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);

  const doLoad = () => {
    if (!isFetching || liveMode !== "on") {
      return;
    }
    getLiveThrougputData({ output, subscriberGroup, mode, bqnId })
      .then((response) => {
        const time = new Date();
        time.setMilliseconds(500);
        time.setHours(time.getHours() + timeOffset);
        const {
          series: parsedResponse,
          totalUp,
          totalDown,
        } = parseThroughputResponse(response);
        setItem({ ...parsedResponse, time });
        setTotal({
          totalUp,
          totalDown,
        });

        const oldFieldsMap = { ...subs.map };
        const newFieldKeys = parsedResponse ? Object.keys(parsedResponse) : [];
        const newFieldNamesSet = new Set([
          ...newFieldKeys,
          ...Object.keys(oldFieldsMap),
        ]);
        setSubs(
          getFieldsAndMap(
            newFieldNamesSet,
            oldFieldsMap,
            colorsSet,
            refColorIndex,
            parsedResponse,
            time,
            timeWindow
          )
        );
      })
      .catch((error) => console.error(error));
  };

  const resetData = () => {
    setSubs({ map: {}, fields: [] });
    setItem(null);
    setTotal({ totalUp: {}, totalDown: {} });
  };

  useEffect(() => {
    setLiveMode("clear");
  }, [output, timeWindow, subscriberGroup, bqnId]);

  useEffect(() => {
    if (
      initTime &&
      liveMode === "on" &&
      Date.now() - initTime > 10 * 60 * 1000 &&
      timeWindow < 100
    ) {
      setLiveMode("off");
      return;
    }

    doLoad();
  }, [seconds]);

  useEffect(() => {
    if (liveMode === "clear") {
      resetData();
      setLiveMode("on");
      setInitTime(Date.now());
    } else {
      setLiveMode(liveMode);
    }
  }, [liveMode]);

  useEffect(() => {
    if (timeOffset !== undefined) {
      setIsFetching(true);
      setInitTime(Date.now());
    }
  }, [timeOffset]);

  const { map, fields } = subs || {};

  const handleMouseEvent = (event) => {
    if (event) {
      setMouseEvent({ ...event });
    } else {
      setMouseEvent(null);
    }
  };

  return (
    <Wrapper>
      <ContextMenu
        timeWindow={timeWindow}
        setTimeWindow={setTimeWindow}
        liveMode={liveMode}
        setLiveMode={setLiveMode}
        output={output}
        setOutput={setOutput}
        subscriberGroup={subscriberGroup}
        setSubscriberGroup={setSubscriberGroup}
        display={display}
        setDisplay={setDisplay}
      />
      {liveMode === "clear" || !map ? null : (
        <div>
          {display === "updown" || display === "downall" ? (
            <Charts
              key="downlink-charts"
              fields={fields}
              fieldsMap={map}
              value={getValue(item, "down")}
              type="down"
              timeOffset={timeOffset}
              onMouseEvent={handleMouseEvent}
              mouseEvent={mouseEvent}
              total={total.totalDown}
              liveMode={liveMode}
              setLiveMode={setLiveMode}
              output={output}
              timeWindow={timeWindow < 100 ? timeWindow : timeWindow / 100}
              display={display}
              subscriberGroup={subscriberGroup}
              initTime={initTime}
              seconds={seconds}
            />
          ) : null}
          {display === "updown" || display === "upall" ? (
            <Charts
              key="uplink-charts"
              fields={fields}
              fieldsMap={map}
              value={getValue(item, "up")}
              type="up"
              timeOffset={timeOffset}
              onMouseEvent={handleMouseEvent}
              mouseEvent={mouseEvent}
              total={total.totalUp}
              liveMode={liveMode}
              setLiveMode={setLiveMode}
              output={output}
              timeWindow={timeWindow < 100 ? timeWindow : timeWindow / 100}
              display={display}
              subscriberGroup={subscriberGroup}
              initTime={initTime}
              seconds={seconds}
            />
          ) : null}
        </div>
      )}
    </Wrapper>
  );
};

export default SubscriberThroughputLiveReport;
